import * as React from 'react';
import { ErrorBoundary } from '../error-boundary';
import styles from './style.module.scss';

type Props = {
    title: string;
    id?: string;
    classes?: string;
    children: React.ReactNode;
}

export enum ToolDialogOpenDirection {
    Left,
    Top,
    Right,
    Bottom
}

export class ToolDialog extends React.Component<Props> {
    private static allToolDialogs: ToolDialog[] = [];

    state = {
        open: false,
        styles: {
            top: 0,
            left: 0,
        }
    }

    private dialog: React.RefObject<HTMLDivElement> = React.createRef();

    componentDidMount() {
        ToolDialog.allToolDialogs.push(this);
    }

    componentWillUnmount() {
        for (let i = 0; i < ToolDialog.allToolDialogs.length; ++i) {
            if (ToolDialog.allToolDialogs[i] === this) {
                ToolDialog.allToolDialogs.splice(i, 1);
            }
        }
    }

    public open = (openRelativeTo: HTMLElement, openDirection: ToolDialogOpenDirection) => {
        const PADDING = 8;

        let x, y;
        const elRect = openRelativeTo.getBoundingClientRect();
        const dialogRect = this.dialog.current.getBoundingClientRect();

        switch (openDirection) {
            case ToolDialogOpenDirection.Top:
                x = elRect.left - dialogRect.width / 2.0 + elRect.width / 2.0;
                y = elRect.top - dialogRect.height - PADDING;
                break;

            case ToolDialogOpenDirection.Right:
                x = elRect.right + PADDING;
                y = elRect.top - dialogRect.height / 2.0 + elRect.height / 2.0;
                break;

            case ToolDialogOpenDirection.Bottom:
                x = elRect.left - dialogRect.width / 2.0 + elRect.width / 2.0;
                y = elRect.bottom + PADDING;
                break;

            case ToolDialogOpenDirection.Left:
            default: 
                x = elRect.left - dialogRect.width - PADDING;
                y = elRect.top - dialogRect.height / 2.0 + elRect.height / 2.0;
                break;
        }

        console.log(`Dialog height: ${dialogRect.height}, Body height: ${window.innerHeight}, Adjusted height ${window.innerHeight - dialogRect.height}`);

        // Min and max keep the tool dialog bounded by the document
        const styles = {
            left: Math.max(62, Math.min(window.innerWidth - dialogRect.width, x)),
            top: Math.max(0, Math.min(window.innerHeight - dialogRect.height - 62, y)),
        };

        this.setState({
            open: true,
            styles: styles
        });
    }

    public close = () => {
        this.setState({ open: false });
    }

    public toggle = () => {
        const open = !this.state.open;
        this.setState({ open: !open });
        if (open) {
            this.onFocus();
        }
    }

    private static draggingElement: ToolDialog = null;
    private static draggingElementOffset: { x: number, y: number } = { x: 0, y: 0 };
    private static hasBoundDragEvents: boolean = null;
    private titleMouseDown = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        ToolDialog.draggingElement = this;

        const rect = e.currentTarget.getBoundingClientRect();
        ToolDialog.draggingElementOffset = {
            x: e.clientX - rect.left,
            y: e.clientY - rect.top,
        }

        if (!ToolDialog.hasBoundDragEvents) {
            ToolDialog.hasBoundDragEvents = true;

            document.addEventListener("mousemove", e => {
                if (ToolDialog.draggingElement) {
                    const bounds = ToolDialog.draggingElement.dialog.current.getBoundingClientRect();

                    // Min and max keep the tool dialog bounded by the document
                    const styles = {
                        left: Math.max(0, Math.min(window.innerWidth - bounds.width, e.clientX - ToolDialog.draggingElementOffset.x)),
                        top: Math.max(0, Math.min(window.innerHeight - bounds.height, e.clientY - ToolDialog.draggingElementOffset.y)),
                    };

                    ToolDialog.draggingElement.setState({ styles: styles });
                }
            });

            document.addEventListener("mouseup", e => {
                ToolDialog.draggingElement = null;
            });
        }
    }

    private onFocus() {
        if (ToolDialog.allToolDialogs[0] === this) { // Already top of the stack
            return;
        }

        for (let i = 0; i < ToolDialog.allToolDialogs.length; ++i) {
            if (ToolDialog.allToolDialogs[i] === this) {
                ToolDialog.allToolDialogs.splice(i, 1);
            }
        }

        ToolDialog.allToolDialogs.unshift(this);

        for (const d of ToolDialog.allToolDialogs) {
            d.setState({});
        }
    }

    public render() {
        const localStyles: React.CSSProperties = { ...this.state.styles };

        localStyles.zIndex = 1000 - ToolDialog.allToolDialogs.indexOf(this);

        return (
            <div ref={this.dialog} id={this.props.id} className={`${styles.toolDialog} ${this.props.classes} ${this.state.open ? "open" : ""}`}
                style={localStyles} onMouseDown={() => this.onFocus()}>
                <header className={styles.header} onMouseDown={e => this.titleMouseDown(e)}>
                    <div className={styles.titleBar}>
                        {this.props.title}
                    </div>
                    <button type="button" className={`pdButton ${styles.closeBtn}`} onClick={e => {e.preventDefault(); this.close();}}>X</button>
                </header>
                <section>
                    <ErrorBoundary>
                        {this.props.children}
                    </ErrorBoundary>
                </section>
            </div>
        );
    }
}
